import React, { FormEvent, useState } from 'react';
import Invoice from '../../../models/Invoice';
import Pagination from '../../../models/Pagination';
import { Table, TableBody, TableCell, TableHead, TableRow, CircularProgress, TableContainer, Stack, Collapse } from '@mui/material';
import { ArrowDropDownOutlined, ArrowDropUpOutlined, ArrowForward } from '@mui/icons-material';
import PaginationButtons from '../../PaginationButtons';
import { formattedCurrency } from '../../../utils/StringUtils';
import moment from 'moment';
import { ISTableTitle, ISTableHeaderCell, ISTableButton, ISTableRow } from '../../Utils/ISTable';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '../../../theme/ThemeContext';
import { UnifiedInvoiceRow } from './InvoicesList';

export interface Row {
  //data: Invoice
  data: UnifiedInvoiceRow
  action?: 'create' | 'update' | 'error';
}

interface Props {
  rows: Row[];
  loading: boolean;
  pagination?: Pagination;
  fullInfo?: boolean;
  adminInfo?: boolean;
  onChangePage?: (page: number) => void;
  showDownloadButton?: boolean;
  onClickInfo?: (data: UnifiedInvoiceRow) => void;
  onClickDownload?: (row: Row) => void;
}

export interface DetailRow {
  data: Invoice
  action?: 'create' | 'update' | 'error';
}

interface DetailProps {
  rows: DetailRow[];
  loading: boolean;
  pagination?: Pagination;
  fullInfo?: boolean;
  adminInfo?: boolean;
  onChangePage?: (page: number) => void;
  showDownloadButton?: boolean;
  onClickInfo?: (data: UnifiedInvoiceRow) => void;
  onClickDownload?: (row: Row) => void;
}

const Invoices: React.FC<Props> = (props) => {
  const showDownloadButton = props.showDownloadButton || false;
  const { t } = useTranslation('invoice')
  const { theme } = useThemeContext()
  const [showFullInfo, setShowFullInfo] = useState<boolean>(!props.adminInfo)

  const mapActionToColor = (action?: string) => {
    switch (action) {
      case 'create': return 'green';
      case 'update': return 'yellow';
      case 'error': return 'red';
      default: return 'default';
    }
  }

  const rowsToShow = /* !props.fullInfo ? props.rows.slice(0, 5) : */ props.rows
  // agregar un checkbox para descarga multiple

  return (
    <Stack
      style={{ display: 'flex', flexDirection: 'column', height: props.fullInfo ? !props.adminInfo ? 'calc(100% - var(--header-height)' : '' : '100%', borderRadius: "10px", margin: "10px", minWidth: props.fullInfo ? "500px" : "770px" }}
      onClick={(event: FormEvent) => { event.preventDefault(); event.stopPropagation(); props.adminInfo ? setShowFullInfo(!showFullInfo) : setShowFullInfo(true) }}>

      <ISTableTitle title={!props.fullInfo ? t('lastrecords') : t('title')}
        sx={{ display: "flex", flexDirection: "row", alignItems: 'center', justifyContent: 'space-between' }}>
        <Stack sx={{ display: "flex", flexDirection: "row" }}>
          {props.adminInfo && showFullInfo ? <ArrowDropUpOutlined sx={{ cursor: 'pointer', color: theme.palette.success.dark }} /> :
            props.adminInfo ? <ArrowDropDownOutlined sx={{ cursor: 'pointer', color: theme.palette.success.dark }} /> : <></>}
        </Stack>
      </ISTableTitle>
      <Collapse style={{ padding: props.adminInfo ? "10px" : 'inherit' }} in={props.adminInfo ? showFullInfo : true}>


        <TableContainer sx={{ overflowX: 'auto', height: !props.fullInfo ? '275px' : '100%' }}>
          <Table size="medium" stickyHeader={props.fullInfo && !props.adminInfo}>
            <TableHead >
              <TableRow selected={true}>
                <ISTableHeaderCell align='center'>{t('date')}</ISTableHeaderCell>
                <ISTableHeaderCell align='center'>{t('number')}</ISTableHeaderCell>
                <ISTableHeaderCell align='center'>{t('dueDate')}</ISTableHeaderCell>
                <ISTableHeaderCell align='right'>{t('total')}</ISTableHeaderCell>
                <ISTableHeaderCell align='center'>{t('type')}</ISTableHeaderCell>
                {showDownloadButton && <ISTableHeaderCell align='right'>{t('invoice')}</ISTableHeaderCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {!props.loading && rowsToShow.map((row, index) =>
                <ISTableRow key={index} color={mapActionToColor(row.action)} sx={{ cursor: 'pointer' }}
                  onClick={(e) => { e.stopPropagation(); showDownloadButton && props.onClickInfo && props.onClickInfo(row.data) }}>
                  <TableCell align='center' sx={{ borderSpacing: "18px 12px" }}>{moment(row.data.fecha).tz("America/Argentina/Buenos_Aires").format('DD/MM/YYYY')}</TableCell>
                  <TableCell align='center'>{row.data.numero}</TableCell>
                  <TableCell align='center'>{moment(row.data.fecha_vencimiento).tz("America/Argentina/Buenos_Aires").format('DD/MM/YYYY')}</TableCell>
                  <TableCell align='right'>{formattedCurrency(row.data.importe_total)}</TableCell>
                  <TableCell align='center'>{row.data.tipo}</TableCell>
                  {showDownloadButton &&
                    <TableCell align='right'>
                      <ISTableButton text={t('download')} status={row.data.status !== null}
                        href='#'
                        onClick={(e) => { e.stopPropagation(); props.onClickDownload && props.onClickDownload(row) }}>
                        <ArrowForward fontSize='inherit' />
                      </ISTableButton>
                    </TableCell>
                  }
                </ISTableRow>
              )}
            </TableBody>
          </Table>
          {props.loading &&
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={25} />
            </div>
          }
          {props.fullInfo && <PaginationButtons pagination={props.pagination} changePage={props.onChangePage} />}
        </TableContainer>
      </Collapse>
    </Stack >
  );
}

export default Invoices

export const InvoiceDetails: React.FC<DetailProps> = (props) => {
  const { t } = useTranslation('invoice')
  return (
    <Stack style={{ display: 'flex', flexDirection: 'column', height: '80%', borderRadius: "10px", margin: "10px", minWidth: "500px" }}>
      <ISTableTitle title={t('titleDetail')} />
      <TableContainer sx={{ overflowX: 'auto', height: '100%' }}>
        <Table size="medium" stickyHeader >
          <TableHead>
            <TableRow selected={true}>
              <ISTableHeaderCell>{t('brand')}</ISTableHeaderCell>
              <ISTableHeaderCell>{t('description')}</ISTableHeaderCell>
              <ISTableHeaderCell align='center'>{t('code')}</ISTableHeaderCell>
              <ISTableHeaderCell align='right'>{t('unitPrice')}</ISTableHeaderCell>
              <ISTableHeaderCell align='right'>{t('quantity')}</ISTableHeaderCell>
              <ISTableHeaderCell align='right'>{t('totalNoTax')}</ISTableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!props.loading &&
              props.rows.map((row, index) => {
                return (
                  <ISTableRow color='default' key={index}>
                    <TableCell>{(row.data as Invoice).marca}</TableCell>
                    <TableCell>{(row.data as Invoice).descripcion}</TableCell>
                    <TableCell align='center'>{(row.data as Invoice).codigo_articulo}</TableCell>
                    <TableCell align='right'>{formattedCurrency((row.data as Invoice).precio_unitario, 3)}</TableCell>
                    <TableCell align='right'>{Number((row.data as Invoice).cantidad as unknown).toFixed(0)}</TableCell>
                    <TableCell align='right'>{formattedCurrency((row.data as Invoice).importe)}</TableCell>
                  </ISTableRow>
                )
              })
            }
          </TableBody>
        </Table>
        {props.loading &&
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={25} />
          </div>
        }
        {<PaginationButtons pagination={props.pagination} />}
      </TableContainer>
    </Stack>
  );
}
